// Forecast configuration component

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Cog6ToothIcon, InformationCircleIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { ForecastConfig } from '@/types/forecast';
import { Tooltip } from '@/components/ui';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '@/styles/datepicker-overrides.css';

interface ForecastConfigurationProps {
  configuration: ForecastConfig;
  onConfigurationChange: (config: ForecastConfig) => void;
  _populationSize: number;
  _forecastSubsetSize?: number;
  disabled: boolean;
}

// Quality presets
const QUALITY_PRESETS = {
  low: { parameterSamples: 50, trajectoriesPerParameter: 20, label: 'Low', description: 'Fast (~2s)' },
  medium: { parameterSamples: 100, trajectoriesPerParameter: 50, label: 'Medium', description: 'Balanced (~10s)' },
  high: { parameterSamples: 200, trajectoriesPerParameter: 100, label: 'High', description: 'Accurate (~40s)' },
};

type QualityLevel = 'low' | 'medium' | 'high' | 'custom';

export const ForecastConfiguration: React.FC<ForecastConfigurationProps> = ({
  configuration,
  onConfigurationChange,
  _populationSize,
  _forecastSubsetSize,
  disabled
}) => {
  // Remember the last confidence intervals state when Monte Carlo was selected
  const lastMonteCarloConfidenceState = useRef<boolean>(configuration.includeConfidenceIntervals);
  
  // Determine current quality level based on configuration values
  const getQualityLevel = useCallback((): QualityLevel => {
    const samples = configuration.parameterSamples || 100;
    const trajectories = configuration.trajectoriesPerParameter || 50;

    for (const [key, preset] of Object.entries(QUALITY_PRESETS)) {
      if (preset.parameterSamples === samples && preset.trajectoriesPerParameter === trajectories) {
        return key as QualityLevel;
      }
    }
    return 'custom';
  }, [configuration.parameterSamples, configuration.trajectoriesPerParameter]);
  
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>(getQualityLevel());
  
  // Update the ref when Monte Carlo is active and confidence intervals change
  useEffect(() => {
    if (configuration.useMonteCarloSimulation) {
      lastMonteCarloConfidenceState.current = configuration.includeConfidenceIntervals;
    }
  }, [configuration.useMonteCarloSimulation, configuration.includeConfidenceIntervals]);
  
  // Update quality level when configuration changes externally
  useEffect(() => {
    const newLevel = getQualityLevel();
    if (newLevel !== qualityLevel) {
      setQualityLevel(newLevel);
    }
  }, [getQualityLevel, qualityLevel]);

  const handleConfigChange = (updates: Partial<ForecastConfig>) => {
    if (disabled) return;
    onConfigurationChange({ ...configuration, ...updates });
  };
  
  const handleQualityChange = (level: QualityLevel) => {
    if (level === 'custom') {
      setQualityLevel('custom');
      return;
    }
    
    const preset = QUALITY_PRESETS[level];
    handleConfigChange({
      parameterSamples: preset.parameterSamples,
      trajectoriesPerParameter: preset.trajectoriesPerParameter
    });
    setQualityLevel(level);
  };
  
  const handleMethodChange = (useMonteCarloSimulation: boolean) => {
    if (useMonteCarloSimulation) {
      // Use sim
      const updates: Partial<ForecastConfig> = {
        useMonteCarloSimulation: true,
        includeConfidenceIntervals: lastMonteCarloConfidenceState.current
      };

      if (!configuration.parameterSamples && !configuration.trajectoriesPerParameter) {
        updates.parameterSamples = QUALITY_PRESETS.medium.parameterSamples;
        updates.trajectoriesPerParameter = QUALITY_PRESETS.medium.trajectoriesPerParameter;
      }
      
      handleConfigChange(updates);
    } else {
      // Analytical
      handleConfigChange({ 
        useMonteCarloSimulation: false
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Information Tooltip */}
      <div className="flex items-center space-x-2">
        <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Forecast Configuration</h3>
        <Tooltip 
          content="Configure forecast parameters including time horizon, calculation method, and confidence interval settings." 
          type="info"
        >
          <InformationCircleIcon className="w-4 h-4 text-blue-600 cursor-help" />
        </Tooltip>
      </div>

      {/* Configuration Controls */}
      <div className={`space-y-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        
        {/* Start Date Selection - Month/Year Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <CalendarIcon className="inline-block w-4 h-4 mr-1" />
            Forecast Start Month
          </label>
          <DatePicker
            selected={configuration.startDate ? new Date(configuration.startDate) : new Date()}
            onChange={(date: Date | null) => {
              if (date) {
                // Force to 1st of month
                const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                handleConfigChange({ startDate: firstOfMonth.toISOString() });
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            // showFullMonthYearPicker
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            disabled={disabled}
            placeholderText="Select month and year"
          />
          <p className="mt-1 text-xs text-gray-500">
            When the forecast period begins (uses 1st of selected month)
          </p>
        </div>
        
        {/* Forecast Period and Granularity */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Forecast Period
            </label>
            <select
              value={configuration.horizonYears}
              onChange={(e) => handleConfigChange({ horizonYears: parseInt(e.target.value) })}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(years => (
                <option key={years} value={years}>
                  {years} year{years !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Time horizon for failure predictions
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Granularity
            </label>
            <select
              value={configuration.granularityMonths}
              onChange={(e) => handleConfigChange({ granularityMonths: parseInt(e.target.value) })}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value={12}>Yearly</option>
              <option value={3}>Quarterly</option>
              <option value={1}>Monthly</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Frequency of forecast data points
            </p>
          </div>
        </div>

        {/* Calculation Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Calculation Method
          </label>
          <div className="space-y-3">
            <label className="flex items-start">
              <input
                type="radio"
                name="method"
                checked={!configuration.useMonteCarloSimulation}
                onChange={() => handleMethodChange(false)}
                disabled={disabled}
                className="mr-3 mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">Analytical</div>
                <div className="text-xs text-gray-600">
                  Fast calculation using expected values
                </div>
              </div>
            </label>
            <label className="flex items-start">
              <input
                type="radio"
                name="method"
                checked={configuration.useMonteCarloSimulation}
                onChange={() => handleMethodChange(true)}
                disabled={disabled}
                className="mr-3 mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">Probabilistic Simulation</div>
                <div className="text-xs text-gray-600">
                  Models uncertainty and replacements with confidence intervals
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Simulation Options  */}
        {configuration.useMonteCarloSimulation && (
          <>
            {/* Simulation Quality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Simulation Quality
              </label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {Object.entries(QUALITY_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => handleQualityChange(key as QualityLevel)}
                    disabled={disabled}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      qualityLevel === key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  onClick={() => handleQualityChange('custom')}
                  disabled={disabled}
                  className={`col-span-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    qualityLevel === 'custom'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Custom
                </button>
              </div>
              <p className="text-xs text-gray-600">
                {((configuration.parameterSamples || 100) * (configuration.trajectoriesPerParameter || 50)).toLocaleString()} simulations
                {qualityLevel !== 'custom' && QUALITY_PRESETS[qualityLevel as keyof typeof QUALITY_PRESETS] && 
                  ` (${QUALITY_PRESETS[qualityLevel as keyof typeof QUALITY_PRESETS].description})`
                }
              </p>
            </div>

            {/* Custom Parameters */}
            {qualityLevel === 'custom' && (
              <>
                {/* Parameter Samples */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parameter Samples
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="range"
                      min="10"
                      max="250"
                      step="10"
                      value={configuration.parameterSamples || 100}
                      onChange={(e) => {
                        handleConfigChange({ parameterSamples: parseInt(e.target.value) });
                      }}
                      disabled={disabled}
                      className="flex-1"
                    />
                    <div className="w-20">
                      <input
                        type="number"
                        min="10"
                        max="250"
                        step="10"
                        value={configuration.parameterSamples || 100}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 10 && value <= 250) {
                            handleConfigChange({ parameterSamples: value });
                          }
                        }}
                        disabled={disabled}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Number of Weibull parameter sets from uncertainty distribution
                  </p>
                </div>

                {/* Trajectories per Parameter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trajectories per Parameter
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="range"
                      min="10"
                      max="500"
                      step="10"
                      value={configuration.trajectoriesPerParameter || 50}
                      onChange={(e) => {
                        handleConfigChange({ trajectoriesPerParameter: parseInt(e.target.value) });
                      }}
                      disabled={disabled}
                      className="flex-1"
                    />
                    <div className="w-20">
                      <input
                        type="number"
                        min="10"
                        max="500"
                        step="10"
                        value={configuration.trajectoriesPerParameter || 50}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 10 && value <= 500) {
                            handleConfigChange({ trajectoriesPerParameter: value });
                          }
                        }}
                        disabled={disabled}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Simulation runs per parameter set
                  </p>
                </div>

                {/* Total Simulations Display */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-900">
                      Total Simulations:
                    </span>
                    <span className="text-sm font-bold text-blue-900">
                      {((configuration.parameterSamples || 100) * (configuration.trajectoriesPerParameter || 50)).toLocaleString()}
                    </span>
                  </div>
                  {((configuration.parameterSamples || 100) * (configuration.trajectoriesPerParameter || 50)) > 50000 && (
                    <p className="mt-2 text-xs text-amber-700">
                      Exceeds 50,000 simulations - calculation may be slow
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Confidence Interval Options */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Confidence Intervals
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={configuration.includeConfidenceIntervals}
                    onChange={(e) => handleConfigChange({ includeConfidenceIntervals: e.target.checked })}
                    disabled={disabled}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Enable</span>
                </label>
              </div>
              
              {configuration.includeConfidenceIntervals && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confidence Level
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="50"
                      max="99"
                      step="1"
                      value={Math.round(configuration.confidenceLevel * 100)}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 50 && value <= 99) {
                          handleConfigChange({ confidenceLevel: value / 100 });
                        }
                      }}
                      disabled={disabled}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-700">%</span>
                    <div className="flex-1 flex space-x-2">
                      {/* Quick select buttons */}
                      {[75, 90, 95].map(level => (
                        <button
                          key={level}
                          onClick={() => handleConfigChange({ confidenceLevel: level / 100 })}
                          disabled={disabled}
                          className={`px-2 py-1 text-xs rounded ${
                            Math.round(configuration.confidenceLevel * 100) === level
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } transition-colors`}
                        >
                          {level}%
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter 50-99%. Lower values give tighter intervals (e.g., 75% for narrower bounds)
                  </p>
                </div>
              )}
            </div>
            
            {/* Planned Failures Option */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">
                    Include Planned Maintenance
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Show scheduled maintenance as planned failures
                  </p>
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={configuration.includePlannedFailures || false}
                    onChange={(e) => handleConfigChange({ includePlannedFailures: e.target.checked })}
                    disabled={disabled}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Enable</span>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};